import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LinearProgress,
  styled
} from '@mui/material';
import {
  Event as EventIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
  ConfirmationNumber as TicketIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[2],
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[6]
  }
}));

const DashboardOverview = () => {
  // Sample data
  const stats = [
    { icon: <EventIcon fontSize="large" />, title: 'Total Events', value: '24', color: 'primary.main' },
    { icon: <PeopleIcon fontSize="large" />, title: 'Attendees', value: '1,240', color: 'secondary.main' },
    { icon: <MonetizationOnIcon fontSize="large" />, title: 'Revenue', value: '$18,750', color: 'success.main' },
    { icon: <TicketIcon fontSize="large" />, title: 'Tickets Sold', value: '3,456', color: 'warning.main' }
  ];

  const upcomingEvents = [
    { id: 1, name: 'Tech Conference 2023', date: 'Oct 15, 2023', progress: 75 },
    { id: 2, name: 'Music Festival', date: 'Nov 5, 2023', progress: 45 },
    { id: 3, name: 'Business Expo', date: 'Dec 2, 2023', progress: 30 }
  ];

  const quickActions = [
    { label: 'Create Event', icon: <AddIcon /> },
    { label: 'Send Notifications', icon: <NotificationsIcon /> },
    { label: 'View Calendar', icon: <CalendarIcon /> }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Dashboard Overview
      </Typography>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: stat.color, mr: 3, width: 56, height: 56 }}>
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {stat.title}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stat.value}
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Grid>
        ))}
      </Grid>
      
      <Grid container spacing={3}>
        {/* Upcoming Events */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Upcoming Events
              </Typography>
              <Button variant="text" color="primary">
                View All
              </Button>
            </Box>
            
            <List>
              {upcomingEvents.map((event) => (
                <React.Fragment key={event.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <EventIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={event.name}
                      secondary={event.date}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    <Box sx={{ width: '150px' }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={event.progress} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {event.progress}% completed
                      </Typography>
                    </Box>
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
        
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Quick Actions
            </Typography>
            
            <Grid container spacing={2}>
              {quickActions.map((action, index) => (
                <Grid item xs={12} key={index}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={action.icon}
                    sx={{
                      p: 2,
                      justifyContent: 'flex-start',
                      borderRadius: 2,
                      borderWidth: 2,
                      '&:hover': { borderWidth: 2 }
                    }}
                  >
                    {action.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
            
            {/* Recent Activity (placeholder) */}
            <Box mt={4}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Recent Activity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 5 new registrations today
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 3 events updated
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Payment received for Tech Conference
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardOverview;